import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store';

// ============================================
// Types
// ============================================
interface AuthGuardProps {
  children: React.ReactNode;
}

// ============================================
// AuthGuard - Protects routes that require authentication
// ============================================
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  if (!isAuthenticated) {
    // Redirect to login, save the intended destination
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
};

// ============================================
// GuestGuard - Redirects authenticated users away from guest-only pages
// ============================================
export const GuestGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  // Get the intended destination from state, or default to profile
  const from = (location.state as { from?: string })?.from || '/profile';

  if (isAuthenticated) {
    // Redirect authenticated users to their intended destination or profile
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

// ============================================
// QuizGuard - Protects quiz routes that require an active session
// Email is critical - must always be present
// ============================================
export const QuizGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const email = useAppSelector((state) => state.user.email);

  // Email is critical - check both Redux state and sessionStorage
  const hasEmail = email || sessionStorage.getItem('userEmail');

  if (!hasEmail) {
    // Redirect to home page if no email - email is required to start quiz
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// ============================================
// SetPasswordGuard - Protects set-password page
// Only accessible after quiz completion with a ref
// ============================================
export const SetPasswordGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const reimbursementRef = useAppSelector((state) => state.payment.reimbursementRef);
  const canEnterSetPassword = sessionStorage.getItem('canEnterSetPassword');

  // Must have a reimbursement ref or session flag
  if (!reimbursementRef && !canEnterSetPassword) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// ============================================
// SettingsGuard - Protects settings page (requires subscription)
// ============================================
export const SettingsGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const subscriptionDetails = useAppSelector(
    (state) => state.user.subscriptionDetails
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Optional: Check for active subscription
  // For now, just require authentication
  if (!subscriptionDetails) {
    // Could redirect to upgrade page or show limited settings
  }

  return <>{children}</>;
};

export default AuthGuard;

